/**
 * Every schema the listing can see, a page at a time — not the first page.
 *
 * `GET /schemas` answers with its own `total`, and a caller that reads only
 * `schemas` takes whatever one page holds. The library asked for 200 against a
 * registry of ~300: the rows come back `created_at DESC`, so the hundred that
 * fell off the end were the oldest — the foundational schemas — and the count
 * beside "Schema library" said 200 as if that were the shelf. Search and the
 * owner filters run over the rows in hand, so a schema cut off here cannot be
 * found by typing its name either.
 *
 * `params` is the rest of the query string, without `limit` / `offset`.
 *
 * A page that fails after the first leaves what was already read: a flaky
 * request costs rows, not the whole library. The first page's failure is
 * raised, because an empty library is the caller's own empty state to show.
 */
export async function listAllSchemas(api, params = '', { pageSize = 200 } = {}) {
  const prefix = params ? `${params}&` : '';
  const out = [];
  for (let offset = 0; ; offset += pageSize) {
    let page;
    try {
      page = await api.request(`/schemas?${prefix}limit=${pageSize}&offset=${offset}`);
    } catch (err) {
      if (offset === 0) throw err;
      return out;
    }
    const rows = (page?.schemas || []).filter((s) => s?.id);
    out.push(...rows);
    const total = typeof page?.total === 'number' ? page.total : out.length;
    // Either test ends the walk on its own. Both are here because they fail
    // differently: a short page is the end of the list, while `total` is what
    // stops us asking for pages a miscounted list would never fill.
    if (rows.length < pageSize || out.length >= total) return out;
  }
}
